/*
 * Páginas de tratamientos — versión española, traducida de la referencia
 * francesa. Redactadas para la paciente: sin promesas de resultado, sin
 * precios y sin afirmaciones no verificables. Las direcciones se definen
 * en i18n/config.js.
 */

const soinsEs = {
    sopk: {
        nav: "SOP",
        title: "SOP: consulta médica en Asnières-sur-Seine",
        description:
            "Síndrome de ovario poliquístico: estudio hormonal, acné, hirsutismo y caída del cabello. Consulta con la Dra. Souhali Massilia en Asnières-sur-Seine, cerca de París.",
        eyebrow: "Trastornos hormonales",
        h1: "SOP: entender primero, tratar después lo que se ve",
        lead: "El síndrome de ovario poliquístico afecta aproximadamente a una de cada diez mujeres. En la consulta de Asnières-sur-Seine buscamos primero la causa del desequilibrio y después tratamos sus efectos visibles: acné, vello, caída del cabello.",
        photo: { file: "bureau.jpg", alt: "La sala de consulta de la clínica en Asnières-sur-Seine" },
        condition: "Síndrome de ovario poliquístico",
        sections: [
            {
                h2: "Reconocer un SOP",
                paragraphes: [
                    "El SOP es un desequilibrio hormonal más que una enfermedad de los ovarios en sentido estricto. Asocia, en grados muy variables, una producción excesiva de andrógenos, una ovulación irregular y a veces resistencia a la insulina. Esa combinación explica signos que en apariencia no guardan relación entre sí.",
                    "Muchas mujeres conviven años con un SOP sin saberlo: les trataron el acné, después el vello y después la caída del cabello, cada cosa por separado, sin relacionar nunca los signos entre sí.",
                ],
                puces: [
                    "ciclos irregulares, espaciados o ausentes",
                    "acné persistente en la edad adulta, sobre todo en la parte baja del rostro",
                    "vello excesivo en cara, cuello, tórax o abdomen (hirsutismo)",
                    "cabello que se afina en la parte superior de la cabeza",
                    "aumento de peso difícil de explicar, cansancio después de comer",
                    "dificultades para concebir",
                ],
            },
            {
                h2: "El estudio en la consulta",
                paragraphes: [
                    "La primera consulta empieza con una entrevista detallada: historia de los ciclos, antecedentes familiares, tratamientos ya probados y repercusión en el día a día. La exploración clínica valora la piel, la distribución del vello y la densidad capilar.",
                    "Después se prescribe un estudio hormonal en sangre, completado si hace falta con un estudio metabólico y una ecografía pélvica. El objetivo no es acumular pruebas, sino confirmar el diagnóstico y descartar otras causas posibles de exceso de andrógenos.",
                    "Los resultados se explican en una consulta de síntesis: qué significa cada valor, qué está implicado en tu caso y qué puede mejorarse.",
                ],
            },
            {
                h2: "Las opciones de tratamiento",
                paragraphes: [
                    "La atención se construye siempre contigo, en función de lo que más te molesta y de tu proyecto vital, incluido un posible deseo de embarazo, que cambia las opciones terapéuticas.",
                    "Según los casos combina un tratamiento médico del desequilibrio hormonal, consejos de estilo de vida dirigidos a la resistencia a la insulina y el tratamiento de los signos visibles: depilación láser o electrólisis para el hirsutismo, un protocolo para el acné, mesoterapia del cuero cabelludo y tratamiento de fondo para la caída del cabello.",
                    "El seguimiento es regular: un desequilibrio hormonal no se corrige en una sesión y los tratamientos se reevalúan a lo largo de los meses.",
                ],
            },
        ],
        faq: [
            [
                "¿El SOP se cura?",
                "No se «cura» un SOP como se cura una infección: es un terreno hormonal que evoluciona con la edad y el estilo de vida. En cambio, sus manifestaciones —acné, vello, ciclos irregulares, caída del cabello— se controlan muy a menudo bien cuando se abordan de forma coherente.",
            ],
            [
                "¿Hay que dejar la píldora para hacer el estudio?",
                "No necesariamente, y desde luego no por iniciativa propia. Algunas determinaciones hormonales no son interpretables con anticoncepción combinada: la médica te indicará, según tu situación, si hay que ajustar el momento del estudio.",
            ],
            [
                "¿Puedo consultar si ya tengo el diagnóstico?",
                "Sí, y es frecuente. Muchas pacientes vienen con un diagnóstico conocido pero con signos cutáneos o capilares nunca tratados, o con un tratamiento que toleran mal.",
            ],
        ],
        related: ["chute-de-cheveux", "epilation-laser", "menopause"],
    },

    menopause: {
        nav: "Menopausia",
        title: "Menopausia y perimenopausia: seguimiento en Asnières-sur-Seine",
        description:
            "Perimenopausia y menopausia: piel más fina, flacidez, sequedad íntima, caída del cabello. Consulta médica en Asnières-sur-Seine, cerca de París.",
        eyebrow: "Salud de la mujer",
        h1: "Perimenopausia y menopausia: un seguimiento, no una fatalidad",
        lead: "El descenso de los estrógenos no afecta solo a los ciclos: se lee en la piel, el cabello, la silueta y el confort íntimo. En la consulta de Asnières-sur-Seine estos cambios se toman en serio y se siguen en conjunto.",
        photo: { file: "cafe.jpg", alt: "La zona de espera de la clínica, con su rincón del café" },
        condition: "Menopausia",
        sections: [
            {
                h2: "Qué cambia y por qué",
                paragraphes: [
                    "La perimenopausia suele empezar varios años antes de la retirada definitiva de la regla. Los ciclos se desordenan, el sueño se fragmenta, el ánimo varía y, en paralelo, la piel se modifica. La producción de colágeno disminuye de forma marcada en los primeros años tras la menopausia: la piel se afina, se deshidrata y pierde firmeza.",
                    "La relación entre estrógenos y andrógenos también cambia, lo que explica la aparición de vello más grueso en el mentón o el labio superior en algunas mujeres, al mismo tiempo que el cabello se afina.",
                ],
                puces: [
                    "piel más seca, más fina, que marca más",
                    "pérdida de firmeza del óvalo facial y del cuello",
                    "cabello menos denso y más quebradizo",
                    "vello más grueso en el rostro",
                    "sequedad e incomodidad íntimas",
                    "manchas pigmentarias más visibles",
                ],
            },
            {
                h2: "La consulta",
                paragraphes: [
                    "La entrevista repasa tus síntomas, tus antecedentes personales y familiares, tus tratamientos actuales y lo que más te pesa en el día a día. La exploración clínica valora la calidad de la piel, la densidad capilar y, si lo deseas, el estado de las mucosas.",
                    "Puede proponerse un estudio analítico. Las indicaciones y contraindicaciones de un eventual tratamiento hormonal de la menopausia se explican sin atajos: no conviene a todas y la decisión se toma tras valorar tu situación personal.",
                ],
            },
            {
                h2: "Lo que puede proponerse",
                paragraphes: [
                    "En la piel, los tratamientos buscan sostener la calidad del tejido: mesoterapia, skinboosters, microneedling, peelings adaptados a pieles maduras, inductores de colágeno e inyecciones de ácido hialurónico cuando la flacidez lo justifica.",
                    "En el cabello, el estudio de la caída orienta hacia una mesoterapia del cuero cabelludo y un tratamiento de fondo. En el confort íntimo, la sequedad y la atrofia reciben una atención específica, que puede plantearse sin vergüenza: es un motivo de consulta frecuente y tratable.",
                    "Cada propuesta se prioriza, se explica y, cuando se trata de un acto estético, va acompañada de un presupuesto por escrito y de un plazo de reflexión.",
                ],
            },
        ],
        faq: [
            [
                "¿A partir de cuándo conviene consultar?",
                "En cuanto los cambios te molesten, sin esperar a la retirada completa de la regla. La perimenopausia es precisamente el momento en que una atención precoz de la piel y del cabello resulta más útil.",
            ],
            [
                "¿Es obligatorio un tratamiento hormonal?",
                "No. El tratamiento hormonal de la menopausia es una opción, sujeta a indicaciones y contraindicaciones precisas. Muchas mujeres reciben seguimiento y alivio sin recurrir a él.",
            ],
            [
                "¿La sequedad íntima se puede tratar de verdad?",
                "Sí. Es uno de los síntomas más frecuentes y de los menos comentados por las pacientes, aunque existen tratamientos eficaces. La consulta permite valorar la situación y proponer una solución adaptada.",
            ],
        ],
        related: ["sopk", "chute-de-cheveux", "melasma"],
    },

    "epilation-laser": {
        nav: "Depilación láser",
        title: "Depilación láser y electrólisis en Asnières-sur-Seine",
        description:
            "Depilación láser de cara y cuerpo, electrólisis del vello claro, atención del hirsutismo. Clínica médica en Asnières-sur-Seine, cerca de París.",
        eyebrow: "Vello",
        h1: "Depilación láser y electrólisis, por una médica",
        lead: "En la consulta de Asnières-sur-Seine la depilación no es un gesto aislado: cuando el vello es excesivo, también buscamos de dónde viene. El láser y la electrólisis se practican en la clínica y a menudo de forma complementaria.",
        photo: { file: "salle.jpg", alt: "La sala equipada para la depilación láser" },
        condition: "Hirsutismo",
        sections: [
            {
                h2: "¿Láser o electrólisis?",
                paragraphes: [
                    "El láser actúa sobre la melanina del vello: es muy eficaz en el vello oscuro y permite tratar con rapidez superficies amplias, como las piernas, las axilas o las ingles. En cambio, no actúa sobre un vello sin pigmento.",
                    "La electrólisis destruye el folículo pelo a pelo, con independencia de su color. Por eso está indicada en el vello blanco, rubio, pelirrojo o resistente, y para los acabados en zonas pequeñas como el mentón o el contorno de los labios.",
                    "Las dos técnicas no se oponen: es frecuente tratar lo esencial con láser y terminar con electrólisis los pelos que el láser no ve. La elección se hace tras explorar la zona, el tipo de vello y tu fototipo.",
                ],
            },
            {
                h2: "Por qué consultar con una médica",
                paragraphes: [
                    "Un vello que aparece o que empeora no siempre es un simple rasgo familiar. Puede revelar un exceso de andrógenos: SOP, trastorno androgénico o efecto de un tratamiento. En ese caso, depilar sin buscar la causa equivale a tratar el signo dejando el mecanismo intacto.",
                    "La médica valora la extensión del vello, busca los signos asociados (acné, ciclos irregulares, caída del cabello) y prescribe un estudio hormonal si es necesario. Tratar la causa hace después que la depilación sea más duradera.",
                    "La consulta es también el momento de comprobar las contraindicaciones, adaptar los parámetros a tu piel y fijar el marco: número previsible de sesiones, intervalo entre ellas y precauciones antes y después.",
                ],
            },
            {
                h2: "Cómo son las sesiones",
                paragraphes: [
                    "Las sesiones se espacian varias semanas para seguir el ciclo de crecimiento del vello: solo responden al tratamiento los folículos en fase activa, por eso un ciclo completo requiere varias sesiones.",
                    "La zona debe rasurarse el día antes, no depilarse con cera ni con pinzas en las semanas previas y protegerse del sol antes y después. La recuperación suele ser sencilla: es frecuente un enrojecimiento transitorio en las horas siguientes.",
                    "El número de sesiones depende de la zona, de la densidad, del color del vello y de la causa del crecimiento. Se estima en consulta y nunca se promete de antemano.",
                ],
            },
        ],
        faq: [
            [
                "¿La depilación láser es definitiva?",
                "Hablamos de depilación duradera más que definitiva. La mayoría del vello tratado no vuelve a crecer, pero un terreno hormonal activo puede reactivar folículos: entonces son necesarias sesiones de mantenimiento espaciadas.",
            ],
            [
                "¿Duele?",
                "La sensación suele describirse como el chasquido de una goma caliente. Varía según la zona y el umbral de cada persona. Los parámetros y el sistema de enfriamiento del equipo se adaptan para que resulte tolerable.",
            ],
            [
                "¿Se puede tratar una piel oscura?",
                "Sí, siempre que el equipo y los parámetros sean adecuados al fototipo. Esa es precisamente la utilidad de una valoración médica previa: determina lo que puede hacerse sin riesgo para tu piel.",
            ],
        ],
        related: ["sopk", "melasma", "chute-de-cheveux"],
    },

    melasma: {
        nav: "Melasma",
        title: "Melasma y manchas: médica en Asnières-sur-Seine",
        description:
            "Melasma, paño del embarazo y manchas marrones: protocolo despigmentante, peelings y fotoprotección. Clínica médica en Asnières-sur-Seine, cerca de París.",
        eyebrow: "Manchas pigmentarias",
        h1: "Melasma: tratar las manchas sin agredir la piel",
        lead: "El melasma es una hiperpigmentación tenaz, a menudo desencadenada por las hormonas y mantenida por el sol. Su abordaje es médico y progresivo, y se apoya tanto en prevenir las recaídas como en tratar las manchas.",
        photo: { file: "peau.jpg", alt: "Detalle de la textura de la piel, ilustración del cuidado cutáneo" },
        condition: "Melasma",
        sections: [
            {
                h2: "Reconocer un melasma",
                paragraphes: [
                    "El melasma se presenta como placas marrones de contornos irregulares y simétricos en la frente, los pómulos, el labio superior o el mentón. Es frecuente durante el embarazo —se habla entonces de paño del embarazo— y con anticoncepción hormonal.",
                    "No debe confundirse con manchas solares, con marcas posinflamatorias tras un acné o con otra lesión pigmentada que requiera una valoración dermatológica. Esa distinción condiciona todo el tratamiento: es lo primero que comprueba la consulta.",
                ],
            },
            {
                h2: "El protocolo de tratamiento",
                paragraphes: [
                    "El tratamiento se apoya en tres pilares inseparables: una fotoprotección estricta y diaria, un tratamiento despigmentante prescrito y supervisado, y tratamientos realizados en la clínica: peelings adaptados y, a veces, microneedling o mesoterapia según el tipo de piel.",
                    "Los gestos demasiado agresivos son contraproducentes: en un melasma, una inflamación mal controlada reactiva la pigmentación. Por eso las sesiones se espacian, son progresivas y se reevalúan en cada etapa.",
                    "La duración es de varios meses. Se anuncia desde la primera consulta, porque la constancia cuenta más que la intensidad de las sesiones.",
                ],
            },
            {
                h2: "Evitar la recaída",
                paragraphes: [
                    "El melasma reaparece con facilidad en cuanto se abandona la protección solar o se retoma un tratamiento hormonal. El mantenimiento forma parte del tratamiento: protector solar alto todo el año, incluso con nubes y detrás de un cristal, y protección física en verano.",
                    "Se repasan tu anticoncepción y tus tratamientos actuales, porque algunos favorecen la pigmentación. Este enfoque global es lo que distingue una atención médica de un simple tratamiento estético.",
                ],
            },
        ],
        faq: [
            [
                "¿El láser está indicado en el melasma?",
                "El láser no es el tratamiento de primera elección: mal escogido, puede agravar un melasma al reactivar la pigmentación. El abordaje empieza por la fotoprotección, los despigmentantes y los peelings adaptados.",
            ],
            [
                "¿Cuánto tiempo hasta notar una diferencia?",
                "Los primeros cambios suelen apreciarse tras varias semanas de tratamiento regular. La valoración se hace con fotografías tomadas en la clínica, con las mismas condiciones de luz.",
            ],
            [
                "¿Puedo tratarme durante el embarazo o la lactancia?",
                "Algunos tratamientos despigmentantes están contraindicados en esas situaciones. La consulta permite adaptar el abordaje y, a menudo, centrarse primero en la protección solar.",
            ],
        ],
        related: ["menopause", "sopk", "epilation-laser"],
    },

    "chute-de-cheveux": {
        nav: "Caída del cabello",
        title: "Caída del cabello: estudio médico en Asnières-sur-Seine",
        description:
            "Caída del cabello en la mujer: estudio de la causa, mesoterapia del cuero cabelludo y tratamiento de fondo. Clínica médica en Asnières-sur-Seine, cerca de París.",
        eyebrow: "Cabello",
        h1: "Caída del cabello: buscar la causa antes de tratar",
        lead: "Una caída del cabello en la mujer casi siempre tiene explicación: hormonal, carencial, inflamatoria o reactiva. El estudio realizado en la consulta de Asnières-sur-Seine busca identificarla antes de iniciar un tratamiento.",
        photo: { file: "mur.jpg", alt: "Pared de la clínica, ambiente cálido y luminoso" },
        condition: "Alopecia",
        sections: [
            {
                h2: "Los distintos tipos de caída",
                paragraphes: [
                    "Se distinguen, por un lado, las caídas bruscas y difusas que aparecen unos meses después de un desencadenante —parto, carencia, dieta, infección, impacto emocional, suspensión de la anticoncepción— y, por otro, las caídas progresivas, que se instalan y modifican la línea del cabello.",
                    "En el segundo caso, la raya se ensancha en la parte superior de la cabeza mientras las sienes se mantienen mucho tiempo: es el perfil clásico de una alopecia de origen androgénico, frecuente en el SOP o después de la menopausia.",
                ],
                puces: [
                    "raya que se ensancha, cuero cabelludo visible con la luz",
                    "coleta claramente menos gruesa",
                    "cabello más fino y más corto, que ya no crece tanto",
                    "caída muy abundante al lavar o al cepillar",
                    "picor o sensación de cuero cabelludo sensible",
                ],
            },
            {
                h2: "El estudio",
                paragraphes: [
                    "La consulta repasa la cronología de la caída, los antecedentes familiares, los tratamientos, la alimentación y los acontecimientos de los últimos meses. La exploración valora la densidad, la línea frontal, el estado del cuero cabelludo y la calidad del tallo.",
                    "Casi siempre se prescribe un análisis de sangre: hierro y ferritina, tiroides, vitamina D y estudio hormonal según el contexto. Es lo que permite distinguir una carencia corregible de un mecanismo hormonal y evitar tratamientos innecesarios.",
                ],
            },
            {
                h2: "Los tratamientos posibles",
                paragraphes: [
                    "La atención corrige primero lo que puede corregirse: déficit de hierro, disfunción tiroidea, desequilibrio hormonal. Esa es la base y a veces basta para detener una caída reactiva.",
                    "Según los casos se añaden un tratamiento local o general prescrito por la médica y sesiones de mesoterapia del cuero cabelludo, que llevan directamente al bulbo principios activos que estimulan el crecimiento.",
                    "El ritmo se explica desde el principio: el cabello crece alrededor de un centímetro al mes, así que un tratamiento capilar se valora a lo largo de varios meses, con fotografías de apoyo.",
                ],
            },
        ],
        faq: [
            [
                "¿Al cabo de cuánto tiempo se ve el resultado?",
                "Hay que contar con varios meses: la primera fase consiste en detener la caída y el crecimiento llega después. Un seguimiento fotográfico en la clínica permite juzgar la evolución de forma objetiva.",
            ],
            [
                "¿La mesoterapia del cuero cabelludo duele?",
                "Las microinyecciones son superficiales y breves. La sensación suele tolerarse bien y la sesión dura unos veinte minutos.",
            ],
            [
                "¿La caída del cabello puede ser el primer signo de un SOP?",
                "Sí, sobre todo si se acompaña de acné, ciclos irregulares o vello excesivo. Esa es una de las razones por las que el estudio capilar incluye una valoración hormonal.",
            ],
        ],
        related: ["sopk", "menopause", "epilation-laser"],
    },

    "acide-hyaluronique": {
        nav: "Ácido hialurónico",
        title: "Inyecciones de ácido hialurónico en Asnières-sur-Seine",
        description:
            "Inyecciones de ácido hialurónico e inductores de colágeno en Asnières-sur-Seine: ojeras, surcos, óvalo facial, labios. Actos realizados por una médica.",
        eyebrow: "Volúmenes y arrugas",
        h1: "Inyecciones de ácido hialurónico, en Asnières-sur-Seine",
        lead: "Devolver soporte allí donde el rostro se ha vaciado, sin transformarlo. Las inyecciones las realiza una médica, tras una consulta, con presupuesto por escrito y plazo de reflexión.",
        photo: { file: "accueil.jpg", alt: "La recepción de Aurassium Clinic en Asnières-sur-Seine" },
        condition: "Envejecimiento cutáneo",
        sections: [
            {
                h2: "Lo que el ácido hialurónico hace y lo que no",
                paragraphes: [
                    "El ácido hialurónico es una molécula presente de forma natural en la piel, donde retiene agua y sostiene los tejidos. Inyectado, rellena un hueco, devuelve volumen o hidrata en profundidad según su densidad.",
                    "No tensa la piel y no sustituye a la cirugía cuando la flacidez es importante. Tampoco detiene el envejecimiento: su efecto es transitorio, de varios meses a más de un año según la zona y el producto.",
                    "Decir lo que un tratamiento no va a hacer forma parte de la consulta. Solo así el resultado se corresponde con lo anunciado.",
                ],
                puces: [
                    "ojeras hundidas y surco lagrimal",
                    "surcos nasogenianos y líneas de marioneta",
                    "pómulos y sienes",
                    "óvalo facial y mentón",
                    "labios: hidratación o redefinición discreta",
                    "manos",
                ],
            },
            {
                h2: "Inductores de colágeno: otra lógica",
                paragraphes: [
                    "Los inductores de colágeno no rellenan: estimulan la producción de colágeno de la propia piel. El resultado se instala poco a poco, a lo largo de varias semanas, y actúa sobre la calidad y la firmeza más que sobre el volumen.",
                    "Ambos enfoques se asocian a menudo: el ácido hialurónico restaura la estructura donde falta y los inductores mejoran la textura y la resistencia de la piel. El plan se establece en consulta, zona por zona.",
                ],
            },
            {
                h2: "Desarrollo, recuperación y seguridad",
                paragraphes: [
                    "La consulta precede siempre a la inyección: antecedentes, tratamientos actuales, expectativas y contraindicaciones. Los productos utilizados son productos sanitarios, inyectados en un marco médico.",
                    "La sesión dura entre quince y treinta minutos según las zonas. Se utiliza una anestesia de contacto o un producto con anestésico local. Son posibles hematomas y edema transitorios: conviene no programar una inyección la víspera de un acontecimiento importante.",
                    "Se propone una cita de control para valorar el resultado una vez reabsorbido el edema y retocar si es necesario.",
                ],
            },
        ],
        faq: [
            [
                "¿Se va a notar?",
                "El objetivo es que te encuentren descansada, no «retocada». Las cantidades se adaptan a tu rostro y pueden aplicarse en varias veces: siempre es posible añadir, pero no es fácil retirar.",
            ],
            [
                "¿Cuánto dura el resultado?",
                "Según la zona, el producto y tu metabolismo, el efecto suele mantenerse de varios meses a más de un año. Después se propone un mantenimiento, sin obligación.",
            ],
            [
                "¿Duele?",
                "La molestia es limitada gracias a la anestesia de contacto y a los productos con anestésico local. Las zonas más sensibles, como los labios, reciben una preparación específica.",
            ],
        ],
        related: ["peau-et-rides", "melasma", "menopause"],
    },

    "peau-et-rides": {
        nav: "Piel y arrugas",
        title: "Tratamiento de la piel y las arrugas en Asnières-sur-Seine",
        description:
            "Calidad de la piel y arrugas en Asnières-sur-Seine: peelings, microneedling, skinboosters, mesoterapia. Protocolo médico a medida, por la Dra. Souhali Massilia.",
        eyebrow: "Calidad de la piel",
        h1: "Tratamiento de la piel y de las arrugas",
        lead: "Tono apagado, poros dilatados, líneas finas, piel que marca: la calidad de la piel se trabaja de forma progresiva, con protocolos adaptados a tu tipo de piel y a la estación.",
        photo: { file: "mur.jpg", alt: "Ambiente luminoso y cálido de la clínica en Asnières-sur-Seine" },
        condition: "Envejecimiento cutáneo",
        sections: [
            {
                h2: "Las técnicas que utilizamos",
                paragraphes: [
                    "El peeling exfolia la piel de forma controlada para reactivar su renovación: superficial para el tono y las líneas finas, medio para las irregularidades más marcadas. Su profundidad se elige según tu piel, nunca según una fórmula estándar.",
                    "El microneedling crea microcanales que estimulan la producción de colágeno y mejoran la textura, las cicatrices de acné y los poros dilatados.",
                    "Los skinboosters y la mesoterapia aportan directamente en la dermis ácido hialurónico muy fluido, vitaminas y antioxidantes: la piel se rellena desde dentro, sin modificar los volúmenes.",
                ],
                puces: [
                    "tono apagado y falta de luminosidad",
                    "líneas finas de deshidratación",
                    "poros dilatados e irregularidades de textura",
                    "cicatrices de acné",
                    "piel que marca y pierde firmeza",
                    "preparación de la piel antes de un acontecimiento",
                ],
            },
            {
                h2: "Construir un protocolo, no acumular sesiones",
                paragraphes: [
                    "La consulta valora tu tipo de piel, su sensibilidad, tu exposición al sol, tu rutina y tus tratamientos actuales. Algunas técnicas no se recomiendan en pieles pigmentadas o propensas al melasma: el protocolo lo tiene en cuenta.",
                    "Las sesiones se espacian y se reevalúan. La intensidad no garantiza el resultado: una piel agredida se defiende, se inflama y pigmenta. La constancia y la fotoprotección logran más que la potencia de un acto aislado.",
                    "Cuando la flacidez supera lo que la calidad de la piel puede corregir, las inyecciones de ácido hialurónico o los inductores de colágeno toman el relevo, como complemento.",
                ],
            },
            {
                h2: "Después de la sesión",
                paragraphes: [
                    "La recuperación depende de la técnica: unas horas de enrojecimiento tras un microneedling o una mesoterapia, unos días de descamación tras un peeling medio. Todo se anuncia antes, para que puedas elegir el momento adecuado.",
                    "Una protección solar alta es imprescindible después, sea cual sea la estación: es lo que protege el beneficio obtenido y evita las marcas posinflamatorias.",
                ],
            },
        ],
        faq: [
            [
                "¿Cuántas sesiones hacen falta?",
                "La mayoría de los protocolos de calidad de la piel prevén de tres a cuatro sesiones separadas por unas semanas y después un mantenimiento. El número exacto se estima en consulta, según el punto de partida.",
            ],
            [
                "¿Se puede tratar en verano?",
                "Algunas técnicas lo permiten con una fotoprotección estricta; otras se posponen al otoño. Es un punto que siempre se aborda, sobre todo en pieles pigmentadas.",
            ],
            [
                "¿Qué diferencia hay entre un skinbooster y el ácido hialurónico inyectado?",
                "El skinbooster hidrata y mejora la calidad de la piel sin crear volumen; el ácido hialurónico de relleno restaura una estructura o un hueco. Ambos pueden asociarse.",
            ],
        ],
        related: ["acide-hyaluronique", "melasma", "acne"],
    },

    "sante-intime": {
        nav: "Salud íntima",
        title: "Sequedad íntima: tratamiento en Asnières-sur-Seine",
        description:
            "Sequedad y atrofia íntimas en Asnières-sur-Seine: consulta médica, atención de la incomodidad asociada a la menopausia. Dra. Souhali Massilia.",
        eyebrow: "Salud íntima",
        h1: "Sequedad íntima: hablar de ella y después tratarla",
        lead: "La sequedad íntima afecta a una de cada dos mujeres después de la menopausia, y mucho antes en algunas. Es un motivo de consulta frecuente, tiene tratamiento y merece algo mejor que el silencio.",
        photo: { file: "cafe.jpg", alt: "El rincón del café de la clínica, zona de espera discreta y acogedora" },
        condition: "Atrofia vulvovaginal",
        sections: [
            {
                h2: "Signos que a menudo se callan",
                paragraphes: [
                    "El descenso de los estrógenos adelgaza la mucosa y reduce la lubricación natural. Aparecen entonces sensación de sequedad, tirantez, escozor, incomodidad diaria y, a menudo, dolor durante las relaciones.",
                    "Estos signos no son una fatalidad ni una consecuencia inevitable de la edad. También aparecen después de ciertos tratamientos, tras un parto, durante la lactancia o con anticoncepción, y empeoran sin atención.",
                ],
                puces: [
                    "sequedad y tirantez en el día a día",
                    "escozor o picor",
                    "dolor durante las relaciones",
                    "infecciones urinarias o candidiasis de repetición",
                    "sensación de flacidez o incomodidad",
                ],
            },
            {
                h2: "La consulta",
                paragraphes: [
                    "La entrevista precisa desde cuándo existen los signos, su repercusión y el contexto hormonal. Se propone una exploración, nunca se impone, y se explica en cada paso.",
                    "La atención es médica: tratamientos locales, hidratantes específicos, tratamiento hormonal local cuando está indicado y no contraindicado, y tratamientos realizados en la clínica para mejorar la trofismo de la mucosa.",
                    "Las opciones se presentan con sus límites y sus contraindicaciones, incluidos los antecedentes que excluyen ciertos tratamientos.",
                ],
            },
            {
                h2: "Un tema tratado como cualquier otro",
                paragraphes: [
                    "La consulta se desarrolla en un marco médico, sin juicios y sin eufemismos. Muchas pacientes esperan años antes de hablar de ello, pensando que no tiene tratamiento.",
                    "La sequedad íntima se inscribe a menudo en un cuadro más amplio —menopausia, piel más seca, cabello más fino— que se aborda en conjunto en la clínica.",
                ],
            },
        ],
        faq: [
            [
                "¿Hay que estar menopáusica para consultar?",
                "No. La sequedad íntima puede aparecer mucho antes: tras un parto, durante la lactancia, con ciertos tratamientos o anticonceptivos, o sin causa hormonal evidente.",
            ],
            [
                "¿Los tratamientos son hormonales?",
                "No necesariamente. Existen opciones no hormonales, locales, y tratamientos realizados en la clínica. La elección depende de tus antecedentes y de tus preferencias.",
            ],
            [
                "¿Puede mejorar de forma duradera?",
                "Sí, con seguimiento: la mucosa vuelve a fragilizarse si se interrumpe la atención. Por eso suele preverse un mantenimiento.",
            ],
        ],
        related: ["menopause", "peau-et-rides", "sopk"],
    },

    acne: {
        nav: "Acné adulto",
        title: "Acné de la mujer adulta: médica en Asnières-sur-Seine",
        description:
            "Acné adulto, acné hormonal y cicatrices: estudio y tratamiento médico en Asnières-sur-Seine. Consulta de la Dra. Souhali Massilia (92600).",
        eyebrow: "Piel",
        h1: "Acné de la mujer adulta: buscar la causa hormonal",
        lead: "Un acné que aparece o persiste después de los 25 no es un acné de adolescente que se ha quedado. En la mujer adulta está muy a menudo relacionado con las hormonas, y de ahí parte la atención.",
        photo: { file: "bureau.jpg", alt: "Cabina de tratamientos de la clínica en Asnières-sur-Seine" },
        condition: "Acné",
        sections: [
            {
                h2: "Reconocer un acné hormonal",
                paragraphes: [
                    "Se localiza sobre todo en la parte baja del rostro: mandíbula, mentón, cuello. Las lesiones suelen ser profundas, inflamatorias y dolorosas, y empeoran en la segunda mitad del ciclo.",
                    "Cuando se acompaña de ciclos irregulares, vello excesivo o caída del cabello, hay que explorar la hipótesis de un exceso de andrógenos, en particular un SOP. Tratar la piel sin explorar ese terreno expone a la recaída al suspender el tratamiento.",
                ],
                puces: [
                    "lesiones en la mandíbula, el mentón y el cuello",
                    "brotes marcados por el ciclo",
                    "lesiones profundas y dolorosas",
                    "marcas y cicatrices persistentes",
                    "asociación con vello excesivo o caída del cabello",
                ],
            },
            {
                h2: "El estudio y el tratamiento",
                paragraphes: [
                    "La consulta repasa la historia del acné, los tratamientos ya probados, la anticoncepción y la rutina de cuidados. Se prescribe un estudio hormonal cuando el cuadro lo justifica.",
                    "El tratamiento combina la atención médica del propio acné y, cuando es necesario, la del desequilibrio hormonal subyacente. Se corrigen las rutinas inadecuadas, frecuentes: una piel acneica agredida se vuelve más reactiva y marca más.",
                    "Una vez controlada la inflamación, las marcas y las cicatrices pueden trabajarse en la clínica con microneedling o peelings adaptados, nunca sobre un acné todavía activo.",
                ],
            },
            {
                h2: "Las marcas que deja el acné",
                paragraphes: [
                    "Hay que distinguir las marcas posinflamatorias, marrones o rojas, que se atenúan con el tiempo y la fotoprotección, de las cicatrices deprimidas, que requieren un tratamiento específico.",
                    "La protección solar es determinante: en una piel acneica, el sol oscurece las marcas de forma duradera. Es el primer consejo que se da en consulta, antes de cualquier sesión.",
                ],
            },
        ],
        faq: [
            [
                "¿Hay que dejar la anticoncepción?",
                "No sin consejo médico. Algunas anticoncepciones empeoran el acné y otras lo mejoran: la decisión se toma en consulta, teniendo en cuenta tu situación en conjunto.",
            ],
            [
                "¿Al cabo de cuánto se nota una mejoría?",
                "Un tratamiento del acné se valora a lo largo de varias semanas o meses. Las primeras semanas pueden incluso incluir una fase de brote, que se anuncia de antemano.",
            ],
            [
                "¿Se pueden tratar las cicatrices al mismo tiempo?",
                "No. Primero se controla la inflamación y después se tratan las marcas. Trabajar una piel todavía activa expone a agravar la pigmentación.",
            ],
        ],
        related: ["sopk", "peau-et-rides", "melasma"],
    },
};

export default soinsEs;
